import { useTranslation } from 'react-i18next';

import {
  AggregationTypeEnum,
  ResourceTypeEnum,
} from '@/shared/components/ui/resource-icon/resource-icon.types';
import { getResourceTypeFromActions } from '@/shared/features/rbac/components/create-policy/policy-editor/policy-editor.utils';
import { useRbacConfig } from '@/shared/features/rbac/context/rbac-config-context';
import { useDefaultResourceType } from '@/shared/features/rbac/hooks/use-default-resource-type/use-default-resource-type';
import { PolicyType, TagFragment } from '@/shared/types/types.generated';

export const usePolicyOptions = () => {
  const { t } = useTranslation();
  const { policies: POLICIES } = useRbacConfig();
  const defaultResourceType = useDefaultResourceType();

  const getResourcePrefix = (resourceType: ResourceTypeEnum) => {
    return `${resourceType.toLowerCase()}:id`;
  };

  const getPolicyResourceType = (policy: PolicyType) =>
    getResourceTypeFromActions(
      POLICIES,
      defaultResourceType,
      policy.action?.[0],
      policy.resource?.[0],
    );

  const getResourceAggregationType = (
    resourceIds: string[],
  ): AggregationTypeEnum => {
    const isGroup = resourceIds?.[0]?.includes(':group:');
    const isTag = resourceIds?.[0]?.includes(':tag:');

    if (isTag) {
      return AggregationTypeEnum.Tag;
    }

    return isGroup ? AggregationTypeEnum.Group : AggregationTypeEnum.Id;
  };

  const getResourceIds = (resources: string[]) => {
    return resources?.map((resourceId) => {
      const split = resourceId.split(':');
      return split[split.length - 1];
    });
  };

  const getActionOptions = (resourceType?: ResourceTypeEnum) => {
    if (!resourceType) {
      return [];
    }

    const policies = POLICIES[resourceType];

    const actions = policies?.map((policy) => {
      const key = policy.replace(':', '.');
      return {
        label: t(`policies:${key}`),
        value: policy,
      };
    });

    return actions;
  };

  const createTagsFromResourceIds = (resourceIds: string[]): TagFragment[] => {
    return resourceIds?.map((resourceId) => ({ name: resourceId })) ?? [];
  };

  const recreatePolicy = (policy: PolicyType) => {
    const aggregationType = getResourceAggregationType(policy.resource);
    const resourceIds = getResourceIds(policy.resource);
    const resourceType = getPolicyResourceType(policy);

    return {
      policy: {
        ...policy,
        resourceType,
        tags:
          aggregationType === AggregationTypeEnum.Tag
            ? createTagsFromResourceIds(resourceIds)
            : undefined,
      },
    };
  };

  return {
    getActionOptions,
    getResourceIds,
    getResourcePrefix,
    getPolicyResourceType,
    getResourceAggregationType,
    recreatePolicy,
  };
};
